import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { axiosWCApiInstance } from '../../configuration/axios/axiosConfiguration';
import { appInsights } from '../../AppInsights';
import { ApiError } from '../../utils/ApiError';
import { convertBase64ToBlob } from '../../utils/helpers';

const getInvoices = async (params) => {
    try {
        const response = await axiosWCApiInstance.get('invoicingListView', { params });
        return response.data;
    } catch (error) {
        console.log(`GetInvoices error=${error}`);
        appInsights.trackException({
            exception: error,
            properties: {
                method: 'getInvoices',
                severityLevel: SeverityLevel.Error,
            },
        });
        if (error.response?.data) {
            throw new ApiError(error.response.data);
        } else if (error.message) {
            throw new ApiError(error.message);
        } else {
            throw ApiError.UnwrappedError(
                'Server Error',
                'There was a problem processing that request.',
                'GetInvoices'
            );
        }
    }
};

const getInvoicesIdList = async (params) => {
    try {
        const response = await axiosWCApiInstance.get('invoicingIDList', { params });
        return response.data;
    } catch (error) {
        console.log(`getInvoicingIdList error=${error}`);
        appInsights.trackException({
            exception: error,
            properties: { method: 'getInvoicingIdList', severityLevel: SeverityLevel.Error },
        });
        if (error.response) {
            throw ApiError.ApiFormattedError(error.response);
        }
        throw ApiError.UnwrappedError(
            'Server Error',
            'There was a problem processing that request.',
            'GetInvoicingIdList'
        );
    }
};

const getPostedInvoices = async (params) => {
    try {
        const response = await axiosWCApiInstance.get('invoices', { params });
        return response.data;
    } catch (error) {
        console.log(`GetPreInvoiced error=${error}`);
        appInsights.trackException({
            exception: error,
            properties: {
                method: 'getPreInvoiced',
                severityLevel: SeverityLevel.Error,
            },
        });
        if (error.response?.data) {
            throw new ApiError(error.response.data);
        } else if (error.message) {
            throw new ApiError(error.message);
        } else {
            throw ApiError.UnwrappedError(
                'Server Error',
                'There was a problem processing that request.',
                'GetPreInvoiced'
            );
        }
    }
};

const getPostedInvoicesIdList = async (params) => {
    try {
        const response = await axiosWCApiInstance.get('/invoices/ids', { params });
        return response.data;
    } catch (error) {
        console.log(`getPostedInvoicesIdList error=${error}`);
        appInsights.trackException({
            exception: error,
            properties: {
                method: 'getPostedInvoicesIdList',
                severityLevel: SeverityLevel.Error,
            },
        });
        if (error.response?.data) {
            throw new ApiError(error.response.data);
        } else if (error.message) {
            throw new ApiError(error.message);
        } else {
            throw ApiError.UnwrappedError(
                'Server Error',
                'There was a problem processing that request.',
                'GetPreInvoiced'
            );
        }
    }
};

const generateInvoices = async (data) => {
    try {
        const response = await axiosWCApiInstance.post('invoice/GenerateGeneralInvoices', data);
        return response.data;
    } catch (error) {
        console.log(`GenerateInvoices error=${error}`);
        appInsights.trackException({
            exception: error,
            properties: {
                method: 'generateInvoices',
                severityLevel: SeverityLevel.Error,
            },
        });
        if (error.response) {
            if (error.response.status === 400) {
                throw new ApiError.InvoiceError(error.response);
            } else {
                throw new ApiError.ApiFormattedError(error.response);
            }
        } else if (error.message) {
            throw new ApiError(error.message);
        } else {
            throw ApiError.UnwrappedError(
                'Server Error',
                'There was a problem processing that request.',
                'GenerateInvoices'
            );
        }
    }
};

const getInvoicePdf = async (invoiceApiId) => {
    try {
        const response = await axiosWCApiInstance.get(`invoice/${invoiceApiId}/pdf`);
        return response.data;
    } catch (error) {
        console.log(`GetInvoicePdf error=${error}`);
        appInsights.trackException({
            exception: error,
            properties: {
                method: 'getInvoicePdf',
                severityLevel: SeverityLevel.Error,
            },
        });
        if (error.response?.data) {
            throw new ApiError(error.response.data);
        } else if (error.message) {
            throw new ApiError(error.message);
        } else {
            throw ApiError.UnwrappedError(
                'Server Error',
                'There was a problem retrieving the invoice PDF.',
                'GetInvoicePdf'
            );
        }
    }
};

const openInvoicePdf = async (invoiceId) => {
    try {
        const result = await getInvoicePdf(invoiceId);
        const file = convertBase64ToBlob(result.base64Data, 'application/pdf');
        window.open(URL.createObjectURL(file));
    } catch (error) {
        console.error(`Error opening pdf. ${error}`);
        throw error;
    }
};

const patchStatuses = async (data) => {
    try {
        const response = await axiosWCApiInstance.patch('invoice/statuses', data);
        return response.data;
    } catch (error) {
        console.log(`patchStatuses error=${error}`);
        appInsights.trackException({
            exception: error,
            properties: { method: 'patchStatuses', severityLevel: SeverityLevel.Error },
        });
        if (error.response) {
            throw ApiError.ApiFormattedError(error.response);
        }
        throw ApiError.UnwrappedError(
            'Server Error',
            'There was a problem processing that request.',
            'PatchStatuses'
        );
    }
};

export {
    getInvoices,
    getInvoicesIdList,
    generateInvoices,
    getPostedInvoices,
    getPostedInvoicesIdList,
    getInvoicePdf,
    patchStatuses,
    openInvoicePdf,
};
